import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/button.scss';

// An option of the split button menu
export interface SplitButtonItem {
    label: string;
    onClick: () => void;
}

interface SplitButtonProps {
    label: string;
    onClick: () => void;
    items: SplitButtonItem[];
    loading?: boolean;
    disabled?: boolean;
    size?: 'extra-small' | 'small' | 'medium' | 'large';
}

// A primary button with a caret that opens a menu with alternative actions
const SplitButton: React.FC<SplitButtonProps> = ({ label, onClick, items, loading = false, disabled = false, size = 'medium' }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Close the menu when clicking outside of the button
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Set the loading and disabled classes
    const stateClass = `${loading ? 'button-loading' : ''} ${disabled ? 'button-disabled' : ''}`;

    return (
        <div className={`split-button ${isOpen ? 'open' : ''}`} ref={containerRef}>
            <button type='button'
                    className={`button button-${size} button-primary split-button-main ${isOpen ? 'button-active' : ''} ${stateClass}`}
                    onClick={onClick}
                    disabled={loading || disabled}>
                <span className={`button-text ${loading ? 'text-hidden' : ''}`}>{t(label)}</span>
                {loading &&
                    <div className='loader-container'>
                        <div className='button-loader' />
                    </div>
                }
            </button>
            <button type='button'
                    className={`button button-${size} button-primary split-button-caret ${isOpen ? 'button-active' : ''} ${stateClass}`}
                    onClick={() => setIsOpen(!isOpen)}
                    disabled={loading || disabled}>
                <FontAwesomeIcon icon={isOpen ? faCaretUp : faCaretDown} />
            </button>
            {isOpen && (
                <div className='dropdown-button-menu split-button-menu'>
                    {items.map((item, index) => (
                        <div key={index} className='dropdown-button-item' onClick={() => { setIsOpen(false); item.onClick(); }}>
                            {t(item.label)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SplitButton;
